import {
  ChevronDown,
  Hamburger,
  Info,
  LogOut,
  Menu,
  User,
  XIcon,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Logo from "src/components/common/Logo";
import Avatar from "src/components/ui/Avatar";
import Button from "src/components/ui/Button";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as authAdminActions from "src/redux/slices/authAdminSlice";
import * as authTenantActions from "src/redux/slices/authAdminSlice";
import { logoutAndRedirect } from "src/utils/auth";
import MenuItems from "./Menu";

interface TopbarProps {
  type?: "admin" | "tenant";
}

const Topbar: React.FC<TopbarProps> = ({ type = "tenant" }) => {
  return (
    <div className="bg-white border-b border-gray-300 p-4">
      {/* Desktop View */}
      <div className="hidden md:block">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo size="small" variant="contained" />
            <Logo size="large" variant="text" />
          </div>

          <div className="flex items-center gap-4">
            <AvatarWrapper userType={type} />
          </div>
        </div>
      </div>

      {/* Mobile View */}
      <div className="md:hidden">
        <div className="flex items-center justify-between">
          <MenuWrapper type={type} />

          <Logo size="large" variant="text" />

          <AvatarWrapper userType={type} variant="mobile" />
        </div>
      </div>
    </div>
  );
};

export default Topbar;

const AvatarWrapper: React.FC<{
  userType?: "admin" | "tenant";
  variant?: "mobile" | "desktop";
}> = ({ userType = "tenant", variant = "desktop" }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const {
    user: { data: adminUser },
  } = useAppSelector((state) => state.authAdmin);
  const {
    user: { data: tenantUser },
  } = useAppSelector((state) => state.authTenant);

  const dispatch = useAppDispatch();

  const user = userType === "admin" ? adminUser : tenantUser;
  const fullName = user?.firstName + " " + user?.lastName;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = () => {
    if (userType === "admin") {
      dispatch(authAdminActions.logout());
      logoutAndRedirect({ isAdmin: true, navigate });
    } else {
      dispatch(authTenantActions.logout());
      logoutAndRedirect({ navigate });
    }
  };

  const routePrefix = userType === "admin" ? "/admin" : "/app";

  if (variant === "mobile") {
    return (
      <div className="relative" ref={ref}>
        <button
          className="flex items-center space-x-2"
          onClick={() => navigate("/app/settings")}
        >
          <Avatar link={user?.avatar || ""} name={user?.firstName || ""} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        className="flex items-center space-x-2"
        onClick={() => setOpen((o) => !o)}
      >
        <Avatar link={user?.avatar || ""} name={user?.firstName || ""} />
        <span className="text-sm font-medium text-gray-700">
          {fullName.split(" ")[0]}
        </span>
        <ChevronDown size={16} className="text-gray-500" />
      </button>

      {open && (
        <div className="absolute right-0 mt-3 w-64 bg-white border rounded-xl shadow-lg p-3 z-50">
          {/* User Info */}
          <div className="flex items-center space-x-3 p-2 border-b pb-3">
            <Avatar link={user?.avatar || ""} name={user?.firstName || ""} />
            <div>
              <p className="font-medium text-gray-800">{fullName}</p>
              <p className="text-sm text-gray-500 text-clip break-all">
                {user?.email}
              </p>
              {user?.roleDetails && (
                <p className="text-sm text-gray-400">
                  Role: {user?.roleDetails?.name}
                </p>
              )}
            </div>
          </div>

          {/* Menu Items */}
          <div className="flex flex-col text-sm mt-2">
            <button
              className="flex items-center space-x-2 hover:bg-gray-50 p-2 rounded-md"
              onClick={() => navigate(`${routePrefix}/settings`)}
            >
              <User size={16} className="text-gray-500" />
              <span className="font-medium">Profile</span>
            </button>
            {/* <button className="flex items-center space-x-2 hover:bg-gray-50 p-2 rounded-md">
                  <Settings size={16} className="text-gray-500" />
                  <span>Account settings</span>
                </button> */}
            <button
              className="flex items-center space-x-2 hover:bg-gray-50 p-2 rounded-md"
              onClick={() => navigate(`${routePrefix}/support`)}
            >
              <Info size={16} className="text-gray-500" />
              <span>Support</span>
            </button>

            <div className="border-t my-2"></div>

            <button
              className="flex items-center space-x-2 text-red-600 hover:bg-red-50 p-2 rounded-md"
              onClick={handleLogout}
            >
              <LogOut size={16} />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const MenuWrapper: React.FC<{
  type: "admin" | "tenant";
}> = ({ type }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <Button
        variant="ghost"
        onClick={() => setOpen(!open)}
        //   onClick={toggleMobileMenu}
      >
        {open ? <XIcon size={24} /> : <Menu size={24} />}
      </Button>

      {open && (
        <div className="absolute -left-4 mt-3 w-screen bg-white border z-50 h-[calc(100vh-64px)] overflow-y-auto">
          <MenuItems type={type} />
        </div>
      )}
    </div>
  );
};
